import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../../../context/ThemeContext";

const API = "https://admin.vascularirdoctors.com/backend";

const Home = () => {
  const { changeBackground } = useContext(ThemeContext);

  const [appointments, setAppointments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [events, setEvents] = useState([]);
  const [memberships, setMemberships] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [
        appointmentsRes,
        treatmentsRes,
        eventsRes,
        membershipsRes,
      ] = await Promise.all([
        fetch(`${API}/appointments`),
        fetch(`${API}/treatments`),
        fetch(`${API}/events`),
        fetch(`${API}/membership-applications`),
      ]);

      const appointmentsData = await appointmentsRes.json();
      const treatmentsData = await treatmentsRes.json();
      const eventsData = await eventsRes.json();
      const membershipsData = await membershipsRes.json();

      setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);
      setTreatments(Array.isArray(treatmentsData) ? treatmentsData : []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setMemberships(Array.isArray(membershipsData) ? membershipsData : []);
    } catch (error) {
      console.log("DASHBOARD FETCH ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Appointments",
      count: appointments.length,
      link: "/appointments",
      className: "dashboard-card-blue",
      icon: "📅",
      subtext: "Manage patient bookings",
    },
    {
      title: "Treatments",
      count: treatments.length,
      link: "/treatments",
      className: "dashboard-card-green",
      icon: "💉",
      subtext: "Treatment pages & services",
    },
    {
      title: "Events",
      count: events.length,
      link: "/events",
      className: "dashboard-card-orange",
      icon: "🎯",
      subtext: "Programs, workshops & updates",
    },
    {
      title: "Memberships",
      count: memberships.length,
      link: "/membership",
      className: "dashboard-card-purple",
      icon: "🧾",
      subtext: "Applications & approvals",
    },
  ];

  return (
    <div className="custom-dashboard-page">
      {/* HEADER */}
      <div className="dashboard-topbar">
        <div>
          <h2 className="dashboard-main-title">Admin Dashboard</h2>
          <p className="dashboard-subtitle">
            Monitor and manage appointments, treatments, events and memberships.
          </p>
        </div>

        <button className="dashboard-refresh-btn" onClick={fetchDashboardData}>
          {loading ? "Refreshing..." : "Refresh Dashboard"}
        </button>
      </div>

      {/* STATS */}
      <div className="row g-4 mb-4">
        {statCards.map((item, index) => (
          <div className="col-xl-3 col-lg-6 col-md-6" key={index}>
            <div className={`custom-stat-card ${item.className}`}>
              <div className="custom-stat-top">
                <div>
                  <p className="stat-label">{item.title}</p>
                  <h3 className="stat-count">{item.count}</h3>
                  <span className="stat-subtext">{item.subtext}</span>
                </div>

                <div className="stat-icon-box">{item.icon}</div>
              </div>

              <div className="stat-footer">
                <Link to={item.link}>Manage Module →</Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CONTENT ROW */}
      <div className="row g-4">
        {/* RECENT APPOINTMENTS */}
        <div className="col-xl-6">
          <div className="dashboard-content-card">
            <div className="dashboard-section-header">
              <h4>Recent Appointments</h4>
              <Link to="/appointments">View All</Link>
            </div>

            <div className="dashboard-list-wrap">
              {appointments.length === 0 ? (
                <div className="empty-state-box">No appointments available</div>
              ) : (
                appointments.slice(0, 5).map((item) => (
                  <div key={item.id} className="dashboard-list-item">
                    <div>
                      <h5>{item.patient_name || "Unnamed Patient"}</h5>
                      <p>{item.service || "No service selected"}</p>
                    </div>
                    <div className="dashboard-item-right">
                      <span>{item.appointment_date || "-"}</span>
                      <small>{item.appointment_time || "-"}</small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RECENT MEMBERSHIPS */}
        <div className="col-xl-6">
          <div className="dashboard-content-card">
            <div className="dashboard-section-header">
              <h4>Recent Memberships</h4>
              <Link to="/membership">View All</Link>
            </div>

            <div className="dashboard-list-wrap">
              {memberships.length === 0 ? (
                <div className="empty-state-box">No membership applications found</div>
              ) : (
                memberships.slice(0, 5).map((item) => (
                  <div key={item.id} className="dashboard-list-item">
                    <div>
                      <h5>
                        {item.first_name || ""} {item.last_name || ""}
                      </h5>
                      <p>{item.membership_type || "Membership Application"}</p>
                    </div>
                    <div className="dashboard-item-right">
                      <span>{item.email || "-"}</span>
                      <small>{item.phone_number || "-"}</small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RECENT TREATMENTS */}
        <div className="col-xl-6">
          <div className="dashboard-content-card">
            <div className="dashboard-section-header">
              <h4>Recent Treatments</h4>
              <Link to="/treatments">View All</Link>
            </div>

            <div className="dashboard-list-wrap">
              {treatments.length === 0 ? (
                <div className="empty-state-box">No treatments available</div>
              ) : (
                treatments.slice(0, 5).map((item) => (
                  <div key={item.id} className="dashboard-list-item">
                    <div>
                      <h5>{item.title || "Untitled Treatment"}</h5>
                      <p>{item.short_description?.substring(0, 55) || "No description"}...</p>
                    </div>
                    <div className="dashboard-item-right">
                      <span>{item.slug || "-"}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RECENT EVENTS */}
        <div className="col-xl-6">
          <div className="dashboard-content-card">
            <div className="dashboard-section-header">
              <h4>Recent Events</h4>
              <Link to="/events">View All</Link>
            </div>

            <div className="dashboard-list-wrap">
              {events.length === 0 ? (
                <div className="empty-state-box">No events available</div>
              ) : (
                events.slice(0, 5).map((item) => (
                  <div key={item.id} className="dashboard-list-item">
                    <div>
                      <h5>{item.title || "Untitled Event"}</h5>
                      <p>{item.location || "No location"}</p>
                    </div>
                    <div className="dashboard-item-right">
                      <span>{item.event_date || "-"}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;